import { AuthAPI } from "@api/Auth.api";
import ButtonVariants from "@core-components/button/ButtonVariants";
import inputsClassnames from "@core-components/inputs/InputClassnames";
import SSHeadline from "@core-components/title/SSHeadline";
import SSWhiteText from "@core-components/title/components/SSWhiteText";
import { EPrivateRoutes } from "@core-types/routes.enum";
import { onEnter } from "@helpers/utils";
import { useAppNavigate } from "@hooks/language/useAppNavigate";
import useAppTranslation from "@hooks/language/useAppTranslation";
import useTimer from "@hooks/useTimer";
import { PinInput, Text, ThemeIcon } from "@mantine/core";
import { useForm } from "@mantine/form";
import { AppNotification } from "@notifications/Notification";
import useLoginModalStore, { ELoginModalState } from "@store/loginModal.store";
import useUserStore from "@store/user.store";
import { IconArrowRight } from "@tabler/icons-react";
import { useState } from "react";

const OTPModalBody: React.FC = () => {
  const { t } = useAppTranslation("components.modals.auth.otp");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const form = useForm({
    initialValues: {
      code: "",
    },
    validate: {
      code: (value: string) =>
        value.length === 6 ? null : t("validation.code"),
    },
  });
  const { open, close, userEmail } = useLoginModalStore();
  const { timer, timerHasEnded, resetTimer } = useTimer(20);
  const userStore = useUserStore();
  const { navigate } = useAppNavigate();

  const verifyOTP = () => {
    form.validate();
    if (form.isValid()) {
      setIsLoading(true);
      AuthAPI.verifyOTP(form.values.code)
        .then((res) => {
          userStore.setToken(res.data.token);

          AppNotification.default({
            title: t("notifications.success.title"),
            message: t("notifications.success.message"),
          });
          close();
          navigate(EPrivateRoutes.ORDERS);
        })
        .catch(() =>
          AppNotification.error({
            title: t("notifications.error.title"),
            message: t("notifications.error.message"),
          }),
        )
        .finally(() => setIsLoading(false));
    }
  };

  const resendOTP = () => {
    resetTimer();
    AuthAPI.login(userEmail).catch(() => {
      AppNotification.error({
        title: t("notifications.resendError.title"),
        message: t("notifications.resendError.message"),
      });
    });
  };

  return (
    <>
      <SSHeadline
        props={{
          title: { classNames: { root: "w-full" }, size: 35 },
          stack: { align: "start", className: "font-inter" },
        }}
        subTitle={
          <span className="mt-[10px] text-[14px] text-primary-dark-text">
            {t("subtitle")}
          </span>
        }
      >
        <>
          <ButtonVariants.Back
            label={t("backButton")}
            className="pl-0 font-inter"
            onClick={() => open(ELoginModalState.LOGIN)}
          />
          <SSWhiteText classNames={{ root: "font-secret-narrow" }}>
            <br />
            {t("title")}
          </SSWhiteText>
        </>
      </SSHeadline>
      <PinInput
        autoFocus
        length={6}
        placeholder={t("codePlaceholder")}
        type="number"
        classNames={{
          ...inputsClassnames,
          root: `${inputsClassnames.root} justify-between`,
          input: `${inputsClassnames.input} border-secret-purple-default`,
        }}
        onKeyDown={(e) => onEnter(e, verifyOTP)}
        {...form.getInputProps("code")}
      />
      <ButtonVariants.Gradient
        isFullWidth
        isLoading={isLoading}
        className="gradient-blue-light-purple overflow-visible rounded-lg"
        onClick={verifyOTP}
        label={
          <>
            {t("submitButton")}{" "}
            <ThemeIcon className="ml-2" color="white" variant="transparent">
              <IconArrowRight />
            </ThemeIcon>
          </>
        }
      />

      <Text
        ta={"center"}
        className="font-inter text-[12px] text-primary-dark-text"
      >
        <Text inherit span>
          {t("resend.noCode")}
          <br />
        </Text>
        {timerHasEnded ? (
          <Text
            span
            inherit
            className="cursor-pointer text-secret-purple-default hover:underline"
            onClick={resendOTP}
          >
            {t("resend.link")}
          </Text>
        ) : (
          <Text span inherit>
            {t("resend.wait", { timer })}
          </Text>
        )}
      </Text>
    </>
  );
};

export default OTPModalBody;
